import { visibleStrategies, type StrategyKey } from './strategy';
import { EV_HINT_COLOR, MANUAL_HATCH_COLOR, rgba } from './chartConfig';
import { GEWEIGERD_COLOR, GEWEIGERD_VERWACHT_FILL, GEWEIGERD_VERWACHT_RAND } from './geweigerd';

type StrategyBands = {
  strategy: StrategyKey; blockedBy?: string | null; predictedBlockedBy?: string | null;
}[];

const Line = ({ color, label }: { color: string; label: string }) => (
  <span className="legend-item"><span className="legend-line" style={{ background: color }}/>{label}</span>
);
const Swatch = ({ color, label }: { color: string; label: string }) => (
  <span className="legend-item"><span className="legend-swatch" style={{ background: color }}/>{label}</span>
);
const Dashed = ({ label }: { label: string }) => (
  <span className="legend-item"><span className="legend-dashed" style={{ color: '#6b7280' }}/><i style={{ color: '#6b7280' }}>{label}</i></span>
);
const Dotted = ({ color, label }: { color: string; label: string }) => (
  <span className="legend-item"><span className="legend-dotted" style={{ color }}/><i style={{ color: '#6b7280' }}>{label}</i></span>
);
// EV-laad band: cyaan met dashed border. CSS-equivalent van de markArea
// styling in chartConfig.evBandMarkArea zodat de legend overeenkomt met wat
// op de chart staat.
const EvBand = ({ label }: { label: string }) => (
  <span className="legend-item">
    <span
      className="legend-swatch"
      style={{
        background: rgba(EV_HINT_COLOR, 0.18),
        border: `1px dashed ${EV_HINT_COLOR}`,
      }}
    />
    {label}
  </span>
);
// Handmatige ingreep: zelfde diagonale arcering als manualBandMarkArea.
// Alleen getoond als er in de timeline een handmatige periode voorkomt.
const ManualBand = () => (
  <span className="legend-item">
    <span
      className="legend-swatch"
      style={{
        background: `repeating-linear-gradient(135deg, ${rgba(MANUAL_HATCH_COLOR, 0.55)} 0 1.2px, transparent 1.2px 5px)`,
        border: `1px dotted ${MANUAL_HATCH_COLOR}`,
      }}
    />
    Handmatige ingreep
  </span>
);
// Laadpoort weigerde een geplande laadbeurt: zelfde grijze arcering als
// strategyBandMarkArea. Alleen getoond als zo'n kwartier in beeld is.
const GeweigerdBand = () => (
  <span className="legend-item">
    <span
      className="legend-swatch"
      style={{
        background: `repeating-linear-gradient(135deg, ${GEWEIGERD_COLOR} 0 1px, #d4d4d4 1px 4px)`,
      }}
    />
    Laden geweigerd
  </span>
);
// Voorspelling: de poort zal een gepland laadslot weigeren. Zelfde stijl als
// strategyBandMarkArea (lichtgrijs, gestippelde rand).
const VerwachtGeweigerdBand = () => (
  <span className="legend-item">
    <span
      className="legend-swatch"
      style={{
        background: GEWEIGERD_VERWACHT_FILL,
        border: `1px dashed ${GEWEIGERD_VERWACHT_RAND}`,
        boxSizing: 'border-box',
      }}
    />
    Laden verwacht geweigerd
  </span>
);
// EV-thuis verticale lijn. Streepje in dezelfde cyaan als de aankomst-marker.
const EvArrival = ({ label }: { label: string }) => (
  <span className="legend-item">
    <span
      className="legend-line"
      style={{
        background: 'transparent',
        borderLeft: `2px dashed ${EV_HINT_COLOR}`,
        height: '0.9em',
        width: 0,
      }}
    />
    <span style={{ color: EV_HINT_COLOR, fontWeight: 600 }}>{label}</span>
  </span>
);

// `bands` filtert de legenda tot strategieën die in de huidige timeline
// voorkomen. Undefined = toon alle 8 (backwards-compat / vóór data binnen is).
export function StrategyLegend({ bands }: { bands?: StrategyBands }) {
  // Een geweigerde band toont geen strategiekleur, dus telt hij niet mee voor
  // welke strategieën in de legenda staan; wel voor de 'Laden geweigerd'-swatch.
  const zichtbaar = bands?.filter(b => !b.blockedBy && !b.predictedBlockedBy);
  const geweigerd = bands?.some(b => b.blockedBy) ?? false;
  const verwacht = bands?.some(b => !b.blockedBy && b.predictedBlockedBy) ?? false;
  return <>
    {visibleStrategies(zichtbaar).map(([k, s]) => <Swatch key={k} color={s.color} label={s.label}/>)}
    {geweigerd && <GeweigerdBand/>}
    {verwacht && <VerwachtGeweigerdBand/>}
  </>;
}

export function PriceLegend({ bands, manual }: { bands?: StrategyBands; manual?: boolean } = {}) {
  // EV-band/arrival bewust niet op de Tarief-chart — alleen op TrendChart
  // (energie-flow) + CombinedChart. Bram-besluit 2026-05-18.
  return (<>
    <Line color="#f87171" label="Inkoop"/>
    <Line color="#60a5fa" label="Verkoop"/>
    <StrategyLegend bands={bands}/>
    {manual && <ManualBand/>}
  </>);
}

export function TrendLegend({ manual }: { manual?: boolean } = {}) {
  return (<>
    <Line color="#facc15" label="PV"/>
    <Line color="#22c55e" label="Batterij"/>
    <Line color="#22d3ee" label="Grid"/>
    <Line color="#7dd3fc" label="Verbruik"/>
    <Line color="#a3a3a3" label="Backup"/>
    <Line color="#ec4899" label="SOC"/>
    <Dashed label="forecast"/>
    <Dotted color="#facc15" label="PV weer"/>
    <EvBand label="EV laden"/>
    <EvArrival label="🔌 EV thuis"/>
    {manual && <ManualBand/>}
  </>);
}

export function CombinedLegend({ bands, manual }: { bands?: StrategyBands; manual?: boolean } = {}) {
  return (<>
    <Line color="#facc15" label="PV"/>
    <Line color="#22c55e" label="Batterij"/>
    <Line color="#ec4899" label="SOC"/>
    <Line color="#22d3ee" label="Grid"/>
    <Line color="#7dd3fc" label="Verbruik"/>
    <Line color="#a3a3a3" label="Backup"/>
    <Line color="#f87171" label="Inkoop"/>
    <Line color="#60a5fa" label="Verkoop"/>
    <Dashed label="forecast"/>
    <Dotted color="#facc15" label="PV weer"/>
    <StrategyLegend bands={bands}/>
    <EvBand label="EV laden"/>
    <EvArrival label="🔌 EV thuis"/>
    {manual && <ManualBand/>}
  </>);
}
